import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Center,
    Flex,
    IconButton,
    Spinner,
    Text,
    useDisclosure,
    useToast,
    Modal,
    ModalOverlay,
    ModalHeader,
    ModalContent,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Input,
    Icon,
    Tooltip,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverFooter,
    CloseButton,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
} from '@chakra-ui/react';
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import FileSaver from 'file-saver';

import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import icons from 'shared/icons';
import { DITable } from 'plugins/DITable';
import {
    useAddClientProductV2Mutation,
    useDeleteClientProductV2Mutation,
    useLazyExportClientProductsQuery,
    useLazyGetAllClientProductsQuery,
    useUpdateClientProductV2Mutation,
} from 'core/API/productsApi';
import { iconTableStyle, linkStyle, tableToolbarStyle } from 'shared/styles';
import { EXCEL_FILE_TYPE, MAX_SIZE_EAN, NON_VISIBLE_FIELDS } from 'shared/const';
import { entries, intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import TablePagination from 'plugins/DITable/Pagination';
import { IBaseProduct, TClientProduct, TEmptyProduct, TRetailerProp } from 'shared/types';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { emptyClientProduct } from 'pages/ProductManagement/consts';
import { isFetchBaseQueryError } from 'utils/erros';
import ARetLangSelector from 'components/atoms/ARetLangSelector';
import { filtreAndOrderColumns } from 'shared/table';
import {
    useGetAvailableLangsSelector,
    useGetCompanyInfoSelector,
    useGetUserInfoSelector,
} from 'core/redux/selectors/authSelectors';
import { isUpcEnabled } from 'shared';
import { useTranslation } from 'helpers/languages/translations';
import { InfoIcon } from '@chakra-ui/icons';
import { isCustomField } from 'utils';
import DeleteCustomColumnsModal from './DeleteCustomColumnsModal';

const MProductsViewer = () => {
    const [productToAdd, setProductToAdd] = useState<TEmptyProduct>(emptyClientProduct);
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [retailer, setRetailer] = useState<TRetailerProp>('default');
    const [lang, setLang] = useState<string>('default');
    const [manuelCustomFields, setManuelCustomFields] = useState<string[]>([]);
    const [customField, setCustomField] = useState<string>('');
    const [customFields, setCustomFields] = useState<string[]>([]);

    const [getClientProducts, { data: clientProducts, isError, isLoading, isFetching }] =
        useLazyGetAllClientProductsQuery();
    const [exportProducts, { isLoading: isExportLoading, isFetching: isExportFetching }] =
        useLazyExportClientProductsQuery();
    const [deleteProduct, { isLoading: isDeleteLoading }] = useDeleteClientProductV2Mutation();
    const [addProduct, { isLoading: isAddLoading }] = useAddClientProductV2Mutation();
    const [updateProduct, { isLoading: isUpdateLoading }] = useUpdateClientProductV2Mutation();
    let emptyProductBeforeSubmit = false;

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isUpdateModalOpen, onOpen: onUpdateModalOpen, onClose: onUpdateModalClose } = useDisclosure();

    const companyInfo = useGetCompanyInfoSelector();
    const availableLangs = useGetAvailableLangsSelector();
    const translate = useTranslation();
    const toast = useToast();

    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(21);

    const isUpc = isUpcEnabled(companyInfo);

    useEffect(() => {
        const getData = async () => {
            await getClientProducts({
                retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                lang,
                isBrut: false,
                isAliases: true,
                omitLang: true,
            });
        };

        getData().catch(console.error);
    }, [retailer, lang]);

    useEffect(() => {
        const customF = clientProducts?.headersRow.filter(isCustomField);

        setCustomFields(customF || []);
    }, [emptyClientProduct, clientProducts?.headersRow]);

    const data: Readonly<any[]> = useMemo(() => {
        if (!isLoading && !isError && clientProducts) {
            return clientProducts.products.map((p) => {
                const tempP = { ...p };
                tempP.upc = tempP.upc || tempP.ean;
                return tempP;
            });
        }
        return [];
    }, [isError, isLoading, clientProducts, clientProducts?.products]);

    const handleClickProduct = (product: TClientProduct) => {
        setModalType('update');
        setProductToAdd(product);
        onOpen();
    };

    const createColumnByType = (type: string): ColumnDef<any, any> => {
        const title = type.includes('name_')
            ? (translate('product.name_l', { lang: type.split('_').pop() }) as string)
            : (translate(`product.${type as keyof IBaseProduct}`) as string);
        switch (type) {
            case 'ean':
                return {
                    header: () => translate(`product.${isUpc ? 'upc' : 'ean'}`) as string,
                    accessorKey: isUpc ? 'upc' : 'ean',
                    cell: (info) => {
                        const productRow = info.row.original as TClientProduct;
                        return (
                            <Box onClick={() => handleClickProduct(productRow)} {...linkStyle}>
                                {info.getValue()}
                            </Box>
                        );
                    },
                } as ColumnDef<any, any>;
            case 'name':
                return {
                    header: () => translate('product.name') as string,
                    accessorKey: 'name',
                    meta: {
                        minWidth: '200px',
                        width: '200px',
                    },
                } as ColumnDef<any, any>;
            default: {
                const headerValue = title === 'keyNotFound' ? type : title;
                return {
                    header: () => (isCustomField(type) ? `${headerValue.slice(8)} (C)` : headerValue),
                    accessorKey: type,
                } as ColumnDef<any, any>;
            }
        }
    };

    const columns: Readonly<ColumnDef<any, any>[]> = useMemo(() => {
        if (!isLoading && !isError && clientProducts && availableLangs) {
            const usedHeader = Array.from(
                new Set(clientProducts.headersRow.concat(availableLangs.map((l) => `name_${l}`))),
            );
            if (usedHeader.filter((h) => h.startsWith('name_')).length === 1) {
                usedHeader.splice(usedHeader.indexOf(usedHeader.find((h) => h.startsWith('name_')) as string), 1);
            }

            const filteredTableHeader: ColumnDef<any, any>[] = filtreAndOrderColumns(usedHeader).map(
                (header: string): ColumnDef<any, any> => {
                    return createColumnByType(header);
                },
            );

            return filteredTableHeader;
        }

        return [];
    }, [isLoading, isError, clientProducts, clientProducts?.headersRow]);

    const defaultColumn = useMemo(
        () => ({
            meta: {
                width: '150px',
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const table = useReactTable<any>({
        data: data as any[],
        columns: columns as ColumnDef<any, any>[],
        defaultColumn,
        sortingFns: SORTING_FNS,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        table.setPageSize(15);
    }, []);

    const renderTableToolbar = () => {
        const PlusIcon = icons.plus;
        const DownloadIcon = icons.download;
        const { setGlobalFilter } = table;

        const exportProductsList = async () => {
            /* eslint-disable @typescript-eslint/no-non-null-assertion */
            try {
                const { data: res, isError: isExportError } = await exportProducts({
                    retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                    lang,
                    isTitleSplit: false,
                    isTemplate: false,
                });

                if (isExportError || !res) throw new Error(translate('product.export.error') as string);

                const blob = new Blob([res], {
                    type: EXCEL_FILE_TYPE,
                });

                FileSaver.saveAs(blob, translate('product.export.fileName', { name: companyInfo!.name_id }) as string);
                toast({
                    title: translate('product.export.success'),
                    status: 'success',
                    isClosable: true,
                });
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleAddProduct = () => {
            setModalType('add');
            setProductToAdd(emptyClientProduct);
            onOpen();
        };

        return (
            <Flex {...tableToolbarStyle} justifyContent="space-between">
                <Flex>
                    <GlobalFilter globalFilter={table.getState().globalFilter} setGlobalFilter={setGlobalFilter} />
                    <IconButton
                        isLoading={isExportLoading || isExportFetching}
                        onClick={intentionalyFloatingPromiseReturn(exportProductsList)}
                        {...iconTableStyle}
                        aria-label="download excel"
                        size="sm"
                        icon={<DownloadIcon />}
                    />
                    <IconButton
                        onClick={handleAddProduct}
                        {...iconTableStyle}
                        aria-label="add product info"
                        size="sm"
                        icon={<Icon as={PlusIcon} />}
                        isDisabled={readonly}
                    />
                    <Tooltip label={translate('product.deleteCustomColumn.header')}>
                        <IconButton
                            onClick={onUpdateModalOpen}
                            {...iconTableStyle}
                            aria-label="remove custom column"
                            size="sm"
                            icon={<Icon as={icons.deleteSweep} />}
                            isDisabled={!customFields.length || readonly}
                        />
                    </Tooltip>
                </Flex>
                <Flex>
                    <ARetLangSelector
                        selectedRetailer={retailer}
                        selectedLang={lang}
                        setSelectedLang={setLang}
                        setSelectedRetailer={setRetailer}
                        omitLang
                    />
                </Flex>
            </Flex>
        );
    };

    const handleDeleteProduct = async () => {
        try {
            const res = await deleteProduct({
                products: [productToAdd.ean as string],
                retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                lang,
                isProductTitle: false,
                omitLang: true,
            });

            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate('product.delete.error') as string);
            }

            onDeleteClose();
            toast({
                title: translate('product.delete.success'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderDeleteModal = () => {
        return (
            <Modal isCentered isOpen={isDeleteOpen} onClose={onDeleteClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text as="h3">{translate('product.delete.header')}</Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" disabled={isDeleteLoading} />
                    <ModalBody fontSize="sm">{translate('product.delete.confirmation')}</ModalBody>

                    <ModalFooter>
                        <Button onClick={onDeleteClose} isDisabled={isDeleteLoading} variant="outline" size="sm" mr="3">
                            {translate('product.delete.cancel')}
                        </Button>
                        <Button
                            onClick={intentionalyFloatingPromiseReturn(handleDeleteProduct)}
                            isLoading={isDeleteLoading}
                            colorScheme="red"
                            size="sm"
                        >
                            {translate('product.delete.apply')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    // TODO: disable fields when its not default default
    const renderAddandEditNewProductModal = () => {
        const handleChangeDefaultInput = (event: ChangeEvent<HTMLInputElement>, key: string) => {
            setProductToAdd({ ...productToAdd, [key]: event.target.value });
        };

        const handleChangeNumberValue = (val: string, key: string) => {
            setProductToAdd({ ...productToAdd, [key]: val });
        };

        const handleDeleteManuelCustom = (field: string) => {
            setManuelCustomFields([...manuelCustomFields.filter((e) => e !== field)]);
            const tempProduct = { ...productToAdd };
            delete tempProduct[field];
            setProductToAdd(tempProduct);
        };

        const renderAttribute = (type: string, isCustom = false, manuelCustom = false) => {
            const concreateType = type === 'ean' ? 'upc' : type;
            const title = isUpc ? concreateType : type;
            const translatedTitle = translate(`product.${title as keyof IBaseProduct}`);
            let fieldTitle = (translatedTitle === 'keyNotFound' ? title : translatedTitle) as string;

            if (isCustom) {
                fieldTitle = fieldTitle.slice(8);
            }

            const isNumber =
                type.includes('recommended_price') ||
                type === 'products_by_packaging' ||
                type === 'average_weekly_sales';

            const nameField = type.startsWith('name');
            const disableName = modalType === 'update' && nameField && retailer !== 'default';
            const tooltip = (
                <Tooltip label={translate('product.tooltip.name_change')} aria-label="product name tooltip">
                    <InfoIcon color="blue.500" cursor="pointer" ml="5px" />
                </Tooltip>
            );

            return (
                <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                    <Flex w="35%" alignItems="center">
                        {manuelCustom && (
                            <CloseButton onClick={() => handleDeleteManuelCustom(type)} size="sm" mr={2} />
                        )}
                        <Text>
                            {fieldTitle}

                            {isCustom && ' (C)'}
                        </Text>
                        {nameField && tooltip}
                    </Flex>

                    <Flex flex="1">
                        {isNumber ? (
                            <NumberInput
                                onChange={(v) => handleChangeNumberValue(v, type)}
                                size="sm"
                                defaultValue={0}
                                value={productToAdd[type]}
                                w="100%"
                                isDisabled={readonly}
                            >
                                <NumberInputField />
                                <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                </NumberInputStepper>
                            </NumberInput>
                        ) : (
                            <Input
                                onChange={(e) => handleChangeDefaultInput(e, type)}
                                size="sm"
                                value={productToAdd[isUpc ? concreateType : type]}
                                isDisabled={(modalType === 'update' && type === 'ean') || disableName || readonly}
                                {...(type === 'ean' ? { maxLength: MAX_SIZE_EAN } : {})}
                            />
                        )}
                    </Flex>
                </Flex>
            );
        };

        const renderForm = () => {
            const form: ReactNode[] = [];
            entries(emptyClientProduct).forEach(([key, _]) => {
                form.push(renderAttribute(key));
                if (key === 'name') {
                    const usedHeader = clientProducts?.headersRow as string[];

                    usedHeader
                        ?.filter((h) => !NON_VISIBLE_FIELDS.includes(h) && h.includes('name_'))
                        .forEach((h) => form.push(renderAttribute(h)));
                }
            });

            customFields.forEach((h) => form.push(renderAttribute(h, true)));

            manuelCustomFields.forEach((f) => form.push(renderAttribute(f, true, true)));
            return form;
        };

        const handleAddProductInfo = async () => {
            const productToSubmit = { ...productToAdd };
            if (emptyProductBeforeSubmit) {
                const fieldsToKeep =
                    retailer === 'default'
                        ? ['ean', 'company', 'country', 'name_split']
                        : ['ean', 'name', 'company', 'country', 'name_en', 'name_split'];
                for (const key in productToSubmit) {
                    if (!fieldsToKeep.includes(key)) {
                        productToSubmit[key] = '';
                    }
                }
            }
            try {
                const res =
                    modalType === 'add'
                        ? await addProduct({
                              product: productToSubmit,
                              isProductTitle: false,
                              retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                              lang,
                              omitLang: true,
                          })
                        : await updateProduct({
                              product: productToSubmit,
                              isProductTitle: false,
                              retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                              lang,
                              headers: clientProducts?.headersRow,
                              omitLang: true,
                          });

                emptyProductBeforeSubmit = false;
                if ('error' in res) {
                    const errorObj = res.error;
                    if (isFetchBaseQueryError(errorObj)) {
                        const errorMsg: string =
                            'message' in (errorObj.data as any)
                                ? (errorObj.data as { message: string }).message
                                : JSON.stringify(errorObj.data);
                        throw new Error(errorMsg);
                    }
                    throw new Error(translate('product.add.error') as string);
                }

                onClose();
                setManuelCustomFields([]);
                setCustomField('');
                const isEanNotdefined =
                    modalType === 'add' &&
                    (res.data as { code: number; created: TClientProduct }).created.is_ean_defined === false;
                toast({
                    title: isEanNotdefined
                        ? translate('product.add.successEanNotDefined', { ean: productToAdd.ean })
                        : translate(modalType === 'add' ? 'product.add.success' : 'product.update.success'),
                    status: isEanNotdefined ? 'warning' : 'success',
                    isClosable: true,
                });
            } catch (err) {
                emptyProductBeforeSubmit = false;
                toast({
                    title: (err as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleDelete = () => {
            onClose();
            onDeleteOpen();
        };

        const renderAddCustomFieldMenu = () => {
            const PlusIcon = icons.plus;
            const handleChangeCustomField = (event: ChangeEvent<HTMLInputElement>) => {
                setCustomField(event.target.value);
            };

            const handleAddCustomField = () => {
                if (!clientProducts?.headersRow.includes(customField)) {
                    setManuelCustomFields([...manuelCustomFields, customField]);
                    setCustomField('');
                } else {
                    toast({
                        title: "Can't use this name / name already exists",
                        status: 'error',
                        isClosable: true,
                    });
                }
            };

            return (
                <Flex mt={4} justifyContent="center">
                    <Popover>
                        {({ onClose: onPopoverClose }) => (
                            <>
                                <Tooltip hasArrow borderRadius={4} label="Add custom field">
                                    <PopoverTrigger>
                                        <IconButton
                                            {...iconTableStyle}
                                            aria-label="add custom field"
                                            size="sm"
                                            icon={<Icon as={PlusIcon} />}
                                        />
                                    </PopoverTrigger>
                                </Tooltip>

                                <PopoverContent>
                                    <PopoverHeader fontWeight="semibold">Add New custom Field</PopoverHeader>
                                    <PopoverBody>
                                        <Flex alignItems="center">
                                            <Text mr="2">Name:</Text>
                                            <Input value={customField} onChange={handleChangeCustomField} size="sm" />
                                        </Flex>
                                    </PopoverBody>
                                    <PopoverFooter display="flex" justifyContent="flex-end">
                                        <Button
                                            onClick={() => {
                                                handleAddCustomField();
                                                onPopoverClose();
                                            }}
                                            isDisabled={customField.length === 0}
                                            size="sm"
                                        >
                                            Apply
                                        </Button>
                                    </PopoverFooter>
                                </PopoverContent>
                            </>
                        )}
                    </Popover>
                </Flex>
            );
        };
        emptyProductBeforeSubmit = false;
        return (
            <Modal size="2xl" isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{translate(`product.${modalType}.header`)}</ModalHeader>
                    <ModalCloseButton isDisabled={isUpdateLoading || isAddLoading} mt="2" />
                    <ModalBody>
                        {renderForm()}
                        {!readonly && renderAddCustomFieldMenu()}
                    </ModalBody>

                    <ModalFooter fontSize="sm" justifyContent="space-between">
                        {!readonly && (
                            <Flex>
                                <Flex>
                                    {modalType === 'update' && (
                                        <Box
                                            {...linkStyle}
                                            color="red.500"
                                            onClick={() => {
                                                emptyProductBeforeSubmit = true;
                                                handleAddProductInfo();
                                            }}
                                        >
                                            {translate('product.update.clear')}
                                        </Box>
                                    )}
                                </Flex>
                                <Flex>
                                    {modalType === 'update' && retailer === 'default' && (
                                        <Box {...linkStyle} color="red.500" onClick={handleDelete}>
                                            {translate('product.update.delete')}
                                        </Box>
                                    )}
                                </Flex>
                            </Flex>
                        )}
                        <Flex>
                            <Button
                                isDisabled={isUpdateLoading || isAddLoading}
                                size="sm"
                                variant="outline"
                                mr={3}
                                onClick={onClose}
                            >
                                {translate('product.delete.cancel')}
                            </Button>
                            {!readonly && (
                                <Button
                                    size="sm"
                                    colorScheme="blue"
                                    isLoading={isAddLoading || isUpdateLoading}
                                    bg="blueMain"
                                    isDisabled={!productToAdd.ean}
                                    onClick={intentionalyFloatingPromiseReturn(handleAddProductInfo)}
                                >
                                    {modalType === 'add' ? 'Add' : 'Apply'}
                                </Button>
                            )}
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    return (
        <Box>
            {renderTableToolbar()}
            {isLoading || isFetching ? (
                <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                    <Spinner />
                </Center>
            ) : (
                <>
                    {columns.length !== 0 && <DITable table={table} isPagination />}
                    <TablePagination table={table} />
                </>
            )}
            {renderDeleteModal()}
            {renderAddandEditNewProductModal()}
            <DeleteCustomColumnsModal
                isOpen={isUpdateModalOpen}
                onClose={onUpdateModalClose}
                customFields={customFields}
            />
        </Box>
    );
};

export default MProductsViewer;
